import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  TrendingUp, 
  ShoppingCart, 
  CreditCard, 
  Banknote, 
  Users, 
  FileText, 
  Briefcase,
  BarChart3,
  Building2,
  LogOut,
  Menu,
  X,
  Settings,
  Shield,
  Receipt,
  History,
  Search,
  Bell,
  Moon,
  Sun,
  Globe,
  Wallet,
  Package,
  Upload,
  Bot,
  ChevronRight,
  ArrowLeftRight,
  ChevronDown,
  CircleDollarSign,
  PieChart,
  ClipboardList,
  UserCog,
  Landmark
} from 'lucide-react';
import { Brain } from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import NotificationPanel from './NotificationPanel';
import AutoLogoutWarning from './AutoLogoutWarning';
import AIAssistant from './AIAssistant';
import PWAInstallBanner from './PWAInstallBanner';
import MobileBottomNav from './MobileBottomNav';
import useAutoLogout from '../hooks/useAutoLogout';
import { dashboardApi } from '../api/dashboard';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useEntity } from '../contexts/EntityContext';
import { usePWAInstall } from '../hooks/usePWAInstall';

export default function Layout({ user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigation, setNavigation] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  
  const { isDark, toggleTheme } = useTheme();
  const { language, t, changeLanguage } = useLanguage();
  const { entities, activeEntityId, activeEntity, switchEntity } = useEntity();
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  
  // Auto logout after 5 minutes of inactivity
  const { showWarning, countdown, stayLoggedIn, logout: autoLogout } = useAutoLogout();
  const { canInstall, isInstalled, install: installPWA } = usePWAInstall();

  // Load notification count
  useEffect(() => {
    const loadNotificationCount = async () => {
      try {
        if (user.role !== 'kasir_lapangan') {
          const data = await dashboardApi.getNotifications();
          setNotificationCount(data.summary?.total || 0);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };
    loadNotificationCount();
    // Refresh every 5 minutes
    const interval = setInterval(loadNotificationCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user.role]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNotificationOpen(false);
        setLangDropdownOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigation with translation keys
  const getNavName = (id) => {
    const navKeys = {
      'dashboard': t('nav.dashboard'),
      'cashflow': t('nav.cashflow'),
      'transactions': t('nav.transactions'),
      'debts': t('nav.debts'),
      'receivables': t('nav.receivables'),
      'investors': t('nav.investors'),
      'taxes': t('nav.taxes'),
      'hr': t('nav.hr'),
      'operational': t('nav.operational'),
      'other-income': t('nav.otherIncome'),
      'reports': t('nav.reports'),
      'profit-loss': 'Laba Rugi',
      'stock-adjustment': 'Adjustment Stok',
      'bulk-import': 'Bulk Import',
      'field-transactions': t('nav.fieldTransactions'),
      'employee-portal': 'Portal Karyawan',
      'owner-expenses': 'Pengeluaran Owner',
      'users': t('nav.userManagement'),
      'audit-log': t('nav.auditLog'),
      'settings': t('nav.settings'),
    };
    return navKeys[id] || id;
  };

  // Full navigation for owner/admin/viewer - GROUPED & CLEAN
  const fullNavigation = [
    { type: 'item', id: 'dashboard', nameKey: 'dashboard', href: '/', icon: LayoutDashboard, visible: true },
    { type: 'divider', label: 'Keuangan' },
    { id: 'cashflow', nameKey: 'cashflow', href: '/cashflow', icon: CircleDollarSign, visible: true },
    { id: 'transactions', nameKey: 'transactions', href: '/transactions', icon: ShoppingCart, visible: true },
    { id: 'debts', nameKey: 'debts', href: '/debts', icon: CreditCard, visible: true },
    { id: 'receivables', nameKey: 'receivables', href: '/receivables', icon: Banknote, visible: true },
    { id: 'investors', nameKey: 'investors', href: '/investors', icon: Landmark, visible: true },
    { id: 'taxes', nameKey: 'taxes', href: '/taxes', icon: FileText, visible: true },
    { type: 'divider', label: 'Operasional' },
    { id: 'hr', nameKey: 'hr', href: '/hr', icon: UserCog, visible: true },
    { id: 'operational', nameKey: 'operational', href: '/operational', icon: Building2, visible: true },
    { id: 'other-income', nameKey: 'other-income', href: '/other-income', icon: Wallet, visible: true },
    { id: 'owner-expenses', nameKey: 'owner-expenses', href: '/owner-expenses', icon: ClipboardList, visible: true },
    { id: 'spsi', name: 'Titipan SPSI', href: '/spsi', icon: ArrowLeftRight, visible: true },
    { type: 'divider', label: 'Laporan' },
    { id: 'reports', nameKey: 'reports', href: '/reports', icon: BarChart3, visible: true },
    { type: 'divider', label: 'Lainnya' },
    { id: 'audit-center', name: 'AI Audit Center', href: '/audit-center', icon: Bot, badge: 'New', visible: true },
    { id: 'settings', nameKey: 'settings', href: '/settings', icon: Settings, visible: true },
  ];

  // Add owner-only menus
  const ownerNavigation = [
    { type: 'item', id: 'dashboard', nameKey: 'dashboard', href: '/', icon: LayoutDashboard, visible: true },
    { type: 'divider', label: 'Keuangan' },
    { id: 'cashflow', nameKey: 'cashflow', href: '/cashflow', icon: CircleDollarSign, visible: true },
    { id: 'transactions', nameKey: 'transactions', href: '/transactions', icon: ShoppingCart, visible: true },
    { id: 'debts', nameKey: 'debts', href: '/debts', icon: CreditCard, visible: true },
    { id: 'receivables', nameKey: 'receivables', href: '/receivables', icon: Banknote, visible: true },
    { id: 'investors', nameKey: 'investors', href: '/investors', icon: Landmark, visible: true },
    { id: 'taxes', nameKey: 'taxes', href: '/taxes', icon: FileText, visible: true },
    { type: 'divider', label: 'Operasional' },
    { id: 'hr', nameKey: 'hr', href: '/hr', icon: UserCog, visible: true },
    { id: 'operational', nameKey: 'operational', href: '/operational', icon: Building2, visible: true },
    { id: 'other-income', nameKey: 'other-income', href: '/other-income', icon: Wallet, visible: true },
    { id: 'owner-expenses', nameKey: 'owner-expenses', href: '/owner-expenses', icon: ClipboardList, visible: true },
    { id: 'spsi', name: 'Titipan SPSI', href: '/spsi', icon: ArrowLeftRight, visible: true },
    { type: 'divider', label: 'Laporan' },
    { id: 'reports', nameKey: 'reports', href: '/reports', icon: BarChart3, visible: true },
    { id: 'intelligence-lab', name: 'Intelligence Lab', href: '/intelligence-lab', icon: Brain, badge: 'Preview', visible: true },
    { type: 'divider', label: 'Administrasi' },
    { id: 'audit-center', name: 'AI Audit Center', href: '/audit-center', icon: Bot, badge: 'New', visible: true },
    { id: 'users', nameKey: 'users', href: '/users', icon: Shield, visible: true },
    { id: 'audit-log', nameKey: 'audit-log', href: '/audit-log', icon: History, visible: true },
    { id: 'data-migration', name: 'Migrasi Data', href: '/data-migration', icon: ArrowLeftRight, visible: true },
    { id: 'bulk-import', name: 'Import & Export', href: '/bulk-import', icon: Upload, visible: true },
    { id: 'settings', nameKey: 'settings', href: '/settings', icon: Settings, visible: true },
  ];

  // Limited navigation for kasir_lapangan
  const kasirNavigation = [
    { type: 'item', id: 'transactions', nameKey: 'transactions', href: '/transactions', icon: ShoppingCart, visible: true },
    { id: 'field-transactions', nameKey: 'field-transactions', href: '/field-transactions', icon: Receipt, visible: true },
    { id: 'receivables', nameKey: 'receivables', href: '/receivables', icon: Banknote, visible: true },
    { id: 'employee-portal', nameKey: 'employee-portal', href: '/employee-portal', icon: Users, visible: true },
    { type: 'divider', label: 'Pengaturan' },
    { id: 'settings', nameKey: 'settings', href: '/settings', icon: Settings, visible: true },
  ];

  const getDefaultNavigation = () => {
    if (user.role === 'kasir_lapangan') return kasirNavigation;
    if (user.role === 'owner') return ownerNavigation;
    return fullNavigation;
  };

  const iconMap = {
    LayoutDashboard, TrendingUp, ShoppingCart, CreditCard, Banknote, Users, FileText, Briefcase, Building2, BarChart3, Shield, Receipt, Settings, History, Wallet, CircleDollarSign, PieChart, ClipboardList, UserCog, Landmark, Package, Upload, ArrowLeftRight
  };

  // State for user permissions
  const [permissions, setPermissions] = useState(null);

  // Load user permissions from API
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const data = await dashboardApi.getMyPermissions();
        setPermissions(data.permissions);
      } catch (error) {
        console.error('Failed to load permissions:', error);
      }
    };
    loadPermissions();
  }, []);

  // Permission-based menu filtering
  const filterNavByPermissions = (navItems, perms) => {
    if (!perms) return navItems;
    
    const permissionMap = {
      'investors': 'can_access_investors',
      'debts': 'can_access_debts',
      'receivables': 'can_access_receivables',
      'taxes': 'can_access_taxes',
      'hr': 'can_access_hr',
      'operational': 'can_access_operational',
      'cashflow': 'can_access_cashflow',
      'transactions': 'can_access_transactions',
      'reports': 'can_access_reports',
      'intelligence-lab': 'can_access_reports',
    };
    
    return navItems.filter(item => {
      if (item.type === 'divider') return true; // Always show dividers
      const requiredPerm = permissionMap[item.id];
      // If no permission required, show the item
      if (!requiredPerm) return true;
      // Check permission (owner always has access)
      return perms[requiredPerm] !== false;
    });
  };

  // Load menu configuration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadMenuConfig = () => {
      // For kasir_lapangan, always use restricted navigation
      if (user.role === 'kasir_lapangan') {
        setNavigation(kasirNavigation);
        return;
      }
      
      const savedConfig = localStorage.getItem('menuConfig');
      let baseNav;
      
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        baseNav = config
          .filter(item => item.type === 'divider' || item.visible)
          .map(item => {
            if (item.type === 'divider') return item;
            return { ...item, icon: iconMap[item.icon] || LayoutDashboard };
          });
        
        if (user.role === 'owner') {
          const hasUserMgmt = baseNav.some(n => n.id === 'users');
          if (!hasUserMgmt) {
            baseNav.push({ id: 'users', name: 'Manajemen User', href: '/users', icon: Shield, visible: true });
          }
        }
      } else {
        baseNav = getDefaultNavigation();
      }
      
      // Apply permission filtering for non-owner users
      if (user.role !== 'owner' && permissions) {
        baseNav = filterNavByPermissions(baseNav, permissions);
      }
      
      setNavigation(baseNav);
    };

    loadMenuConfig();
    
    const handleMenuConfigChange = () => loadMenuConfig();
    window.addEventListener('menuConfigChanged', handleMenuConfigChange);
    return () => window.removeEventListener('menuConfigChanged', handleMenuConfigChange);
  }, [user.role, permissions]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-slate-900 transition-colors" data-testid="layout">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-[#1A4D2E] to-[#122E1C] transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-5 py-5 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <img
                  src="/kansha-icon-gold-only.png"
                  alt="Kansha"
                  className="w-11 h-11 flex-shrink-0"
                />
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-white leading-none tracking-wide truncate" style={{ fontFamily: 'Georgia, serif' }}>
                    KANSHA
                  </h1>
                  <p className="text-[8px] text-amber-300/80 font-medium tracking-[0.2em] uppercase mt-1">Agri · Tech · Trade · IT</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white/60 hover:text-white"
                data-testid="close-sidebar-button"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Entity Switcher */}
          {entities.length > 0 && (
            <div className="px-3 py-3 border-b border-white/8">
              <div className="relative">
                <button
                  onClick={() => setEntityDropdownOpen(!entityDropdownOpen)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/8 hover:bg-white/12 transition-all duration-200 text-left group"
                  data-testid="entity-switcher"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-400/20 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-400/30 transition-colors">
                    <Building2 size={15} className="text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {activeEntityId === 'all' ? 'Konsolidasi (Semua Entity)' : (activeEntity?.name || 'Pilih Entitas')}
                    </p>
                    <p className="text-[10px] text-white/40 uppercase tracking-wider">
                      {activeEntityId === 'all' ? 'Lintas CV · PT · Perorangan' : (activeEntity?.type || '')}
                    </p>
                  </div>
                  <ChevronDown size={14} className={`text-white/40 transition-transform duration-200 ${entityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {entityDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1.5 bg-[#0D2818] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-sm">
                    {/* Opsi Konsolidasi — lintas semua entity untuk audit/laporan */}
                    <button
                      onClick={() => { switchEntity('all'); setEntityDropdownOpen(false); window.location.reload(); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/10 transition-colors border-b border-white/5 ${activeEntityId === 'all' ? 'bg-amber-500/20' : ''}`}
                      data-testid="entity-option-all"
                    >
                      <div className={`w-2 h-2 rounded-full ${activeEntityId === 'all' ? 'bg-amber-400 shadow-sm shadow-amber-400/50' : 'bg-amber-400/40'}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm truncate ${activeEntityId === 'all' ? 'text-amber-300 font-bold' : 'text-amber-200/90 font-medium'}`}>
                          Konsolidasi (Semua Entity)
                        </p>
                        <p className="text-[10px] text-amber-200/40 uppercase">Lintas CV · PT · Perorangan</p>
                      </div>
                    </button>
                    {entities.map((entity) => (
                      <button
                        key={entity.id}
                        onClick={() => { switchEntity(entity.id); setEntityDropdownOpen(false); window.location.reload(); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/10 transition-colors ${activeEntityId === entity.id ? 'bg-white/12' : ''}`}
                        data-testid={`entity-option-${entity.id}`}
                      >
                        <div className={`w-2 h-2 rounded-full transition-colors ${activeEntityId === entity.id ? 'bg-amber-400 shadow-sm shadow-amber-400/50' : 'bg-white/20'}`} />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm truncate ${activeEntityId === entity.id ? 'text-amber-300 font-medium' : 'text-white/80'}`}>{entity.name}</p>
                          <p className="text-[10px] text-white/30 uppercase">{entity.type}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-3 overflow-y-auto scrollbar-thin" data-testid="sidebar-nav">
            {navigation.map((item, idx) => {
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${idx}`} className="mt-4 mb-1.5 px-3">
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.15em]">{item.label}</p>
                  </div>
                );
              }
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              const displayName = item.name || getNavName(item.nameKey || item.id);
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150 mb-0.5 relative ${
                    isActive
                      ? 'bg-white/15 text-amber-300'
                      : 'text-white/60 hover:bg-white/8 hover:text-white/90'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-amber-400 rounded-r-full" />
                  )}
                  <Icon size={17} className={`mr-3 flex-shrink-0 transition-colors ${isActive ? 'text-amber-300' : 'text-white/40 group-hover:text-white/70'}`} />
                  {displayName}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="px-3 py-3 border-t border-white/10 bg-black/10">
            <div className="flex items-center mb-3 px-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-amber-400/20">
                {user.full_name.charAt(0).toUpperCase()}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user.full_name}</p>
                <p className="text-[10px] text-amber-300/80 font-medium">
                  {{ owner: 'Pemilik', admin: 'Administrator', viewer: 'Peninjau', kasir_lapangan: 'Kasir Lapangan' }[user.role] || user.role}
                </p>
              </div>
            </div>
            
            {/* Install PWA Button */}
            {canInstall && (
              <button
                onClick={installPWA}
                className="flex items-center w-full px-3 py-2.5 text-[13px] font-semibold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors mb-1 border border-emerald-500/20"
                data-testid="install-pwa-btn"
              >
                <Package size={16} className="mr-3" />
                Install Aplikasi
              </button>
            )}
            {isInstalled && (
              <div className="flex items-center px-3 py-1.5 text-[11px] text-emerald-400/60 mb-1">
                <Package size={13} className="mr-2" />
                Terinstall
              </div>
            )}
            
            <button
              onClick={onLogout}
              className="flex items-center w-full px-3 py-2 text-[13px] font-medium text-red-300/60 hover:bg-red-500/10 hover:text-red-300 rounded-lg transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={16} className="mr-3" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#1A4D2E]/30 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="bg-white dark:bg-slate-800 border-b border-stone-200 dark:border-slate-700 sticky top-0 z-30">
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-stone-500 hover:text-[#1A4D2E] dark:text-slate-400 dark:hover:text-slate-200 mr-4"
                data-testid="open-sidebar-button"
              >
                <Menu size={24} />
              </button>
              <h2 className="text-lg font-semibold text-[#0F172A] dark:text-white flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {(() => {
                  const currentNav = navigation.find(item => item.href === location.pathname);
                  if (currentNav) {
                    return currentNav.name || getNavName(currentNav.nameKey || currentNav.id);
                  }
                  return t('nav.dashboard');
                })()}
              </h2>
              
              {/* Language Selector */}
              <div className="relative mr-2">
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-1 p-2 text-stone-500 hover:text-[#1A4D2E] dark:text-slate-400 dark:hover:text-slate-200 hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Change Language"
                >
                  <Globe size={20} />
                  <span className="text-xs font-medium uppercase">{language}</span>
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden z-50">
                    <button
                      onClick={() => { changeLanguage('id'); setLangDropdownOpen(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 ${language === 'id' ? 'bg-[#1A4D2E]/10 dark:bg-slate-700 font-medium text-[#1A4D2E]' : ''} text-stone-700 dark:text-slate-300`}
                    >
                      ID Indonesia
                    </button>
                    <button
                      onClick={() => { changeLanguage('en'); setLangDropdownOpen(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 ${language === 'en' ? 'bg-[#1A4D2E]/10 dark:bg-slate-700 font-medium text-[#1A4D2E]' : ''} text-stone-700 dark:text-slate-300`}
                    >
                      EN English
                    </button>
                  </div>
                )}
              </div>
              
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-stone-500 hover:text-[#1A4D2E] dark:text-slate-400 dark:hover:text-slate-200 hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 rounded-lg mr-2 transition-colors"
                title={isDark ? 'Light Mode' : 'Dark Mode'}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-stone-500 hover:text-[#1A4D2E] dark:text-slate-400 dark:hover:text-slate-200 hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 rounded-lg mr-2 transition-colors"
                title={`${t('common.search')} (Ctrl+K)`}
              >
                <Search size={20} />
              </button>
              
              {/* Notification Button - Hide for Kasir */}
              {user.role !== 'kasir_lapangan' && (
                <button
                  onClick={() => setNotificationOpen(true)}
                  className="p-2 text-stone-500 hover:text-[#1A4D2E] dark:text-slate-400 dark:hover:text-slate-200 hover:bg-[#1A4D2E]/5 dark:hover:bg-slate-700 rounded-lg relative transition-colors"
                  title={t('notifications.title')}
                >
                  <Bell size={20} />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="px-4 py-6 sm:px-6 lg:px-8 pb-24 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation (hanya tampil di < lg) */}
      <MobileBottomNav onOpenSidebar={() => setSidebarOpen(true)} />

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      
      {/* Notification Panel */}
      <NotificationPanel isOpen={notificationOpen} onClose={() => setNotificationOpen(false)} />
      
      {/* Auto Logout Warning Modal */}
      <AutoLogoutWarning 
        show={showWarning} 
        countdown={countdown} 
        onStay={stayLoggedIn} 
        onLogout={autoLogout} 
      />
      
      {/* AI Assistant Floating Button & Chat */}
      <AIAssistant />

      {/* PWA Install Banner (auto shows after 30s if installable) */}
      <PWAInstallBanner />
    </div>
  );
}